// In production behind nginx, use the same HTTPS origin and let nginx proxy
// /api to the Node backend. Keep the direct backend port for local dev.
const localHosts = ['localhost', '127.0.0.1'];
const directFrontendPorts = new Set(['3005']);
const API_BASE =
  process.env.REACT_APP_API_URL ||
  (localHosts.includes(window.location.hostname) || directFrontendPorts.has(window.location.port)
    ? `${window.location.protocol}//${window.location.hostname}:5003/api`
    : '/api');

const retryableStatuses = new Set([408, 425, 429, 500, 502, 503, 504]);
const invalidSessionReasons = new Set([
  'multiple_devices',
  'session_revoked',
  'account_inactive',
  'account_terminated',
  'platform_access_revoked',
]);
const wait = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));
const SESSION_EXPIRED_EVENT = 'recon:session-expired';
const SESSION_EXPIRY_UPDATED_EVENT = 'recon:session-expiry-updated';
const SESSION_EXPIRED_MESSAGE = 'Session timed out. Please log in again.';
const SESSION_NOTICE_KEY = 'reconSessionNotice';
const SESSION_EXPIRES_HEADER = 'X-Session-Expires-At';
const SESSION_CAN_EXTEND_HEADER = 'X-Session-Can-Extend';
const SERVER_TIME_HEADER = 'X-Server-Time';
const ABASPHERE_API_ERROR_EVENT = 'recon:abasphere-api-error';
let sessionExpiredDispatched = false;
const pendingMutationIdentities = new Map();
const randomUuid = () => window.crypto?.randomUUID?.()
  || 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const value = Math.floor(Math.random() * 16);
    return (character === 'x' ? value : ((value & 3) | 8)).toString(16);
  });
const mutationFingerprint = (url, method, body) => `${method}:${String(url)}:${String(body || '')}`;
const withAbaSphereMutationIdentity = (url, method, options) => {
  if (!isABASphereRequest(url) || !['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) return options;
  const fingerprint = mutationFingerprint(url, method, options.body);
  const existing = pendingMutationIdentities.get(fingerprint);
  const identity = existing || { idempotencyKey: randomUuid(), correlationId: randomUuid() };
  pendingMutationIdentities.set(fingerprint, identity);
  return {
    ...options,
    headers: {
      ...(options.headers || {}),
      'Idempotency-Key': options.headers?.['Idempotency-Key'] || identity.idempotencyKey,
      'X-Correlation-ID': options.headers?.['X-Correlation-ID'] || identity.correlationId,
    },
    mutationFingerprint: fingerprint,
  };
};

const isABASphereRequest = url => /(?:^|\/)api\/(?:abasphere|aba)(?:[/?#]|$)/i.test(String(url || ''));
const errorGuidanceForStatus = status => {
  if (status === 400 || status === 422) return 'Review the required fields and the validation details below, then try again.';
  if (status === 403) return 'Your current role is not authorized for this action. Contact an administrator if this access is required.';
  if (status === 404) return 'The record may have changed or been removed. Close the popup, refresh the screen, and try again.';
  if (status === 409) return 'The record or workflow state changed. Refresh it before retrying; published records must be changed through a successor draft.';
  if (status >= 500) return 'The server could not complete the request. Keep the popup open and share these details with support if retrying fails.';
  return 'Review the details below and try again.';
};
const normalizeABASphereIssues = payload => {
  const raw = payload?.issues || payload?.validationIssues || payload?.validationErrors || payload?.errors || [];
  if (Array.isArray(raw)) return raw.map((issue, index) => {
    if (typeof issue === 'string') return { message: issue };
    if (!issue || typeof issue !== 'object') return { message: `Validation issue ${index + 1}` };
    return {
      code: issue.code || issue.errorCode || '',
      field: issue.field || issue.path || issue.property || '',
      message: issue.message || issue.detail || issue.description || JSON.stringify(issue),
    };
  });
  if (raw && typeof raw === 'object') {
    return Object.entries(raw).flatMap(([field, value]) => (Array.isArray(value) ? value : [value])
      .map(item => ({ field, message: typeof item === 'string' ? item : JSON.stringify(item) })));
  }
  return [];
};
const notifyABASphereApiError = async ({ url, method, response, error }) => {
  if (!isABASphereRequest(url)) return;
  let payload = {};
  if (response?.clone) {
    try { payload = await response.clone().json(); } catch { /* A non-JSON error still receives a useful fallback. */ }
  }
  const status = Number(response?.status || 0);
  const message = payload?.message || payload?.detail || payload?.error
    || error?.message || `The ${method || 'request'} request could not be completed${status ? ` (HTTP ${status})` : ''}.`;
  window.dispatchEvent(new CustomEvent(ABASPHERE_API_ERROR_EVENT, { detail: {
    message: String(message),
    guidance: errorGuidanceForStatus(status),
    issues: normalizeABASphereIssues(payload),
    status,
    method: String(method || 'GET').toUpperCase(),
    url: String(url || ''),
    occurredAt: new Date().toISOString(),
  } }));
};

// Credential failures are expected while signing in or resetting a password.
// Session-management routes (notably extend-session) are protected routes and
// must still force logout when the server reports an invalid session.
const isCredentialRequest = url => (
  /\/auth\/(?:login|request-otp|reset-password)(?:[/?#]|$)/i.test(String(url || ''))
);

const invalidSessionReason = payload => {
  const rawReason = payload?.code || payload?.reason || payload?.errorCode || payload?.error;
  const normalized = String(rawReason || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return invalidSessionReasons.has(normalized) ? normalized : '';
};

const notifySessionExpired = () => {
  if (sessionExpiredDispatched) return;
  sessionExpiredDispatched = true;
  try {
    window.sessionStorage.setItem(SESSION_NOTICE_KEY, SESSION_EXPIRED_MESSAGE);
  } catch {
    // The event still signs the user out when session storage is unavailable.
  }
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, {
    detail: { message: SESSION_EXPIRED_MESSAGE },
  }));
};

const notifySessionExpiryUpdated = (expiresAt, { canExtend, serverTime } = {}) => {
  const expiration = Date.parse(expiresAt || '');
  if (!Number.isFinite(expiration)) return;
  const normalizedCanExtend = typeof canExtend === 'boolean'
    ? canExtend
    : (/^(true|1)$/i.test(String(canExtend || ''))
      ? true
      : (/^(false|0)$/i.test(String(canExtend || '')) ? false : undefined));
  const normalizedServerTime = Number.isFinite(Date.parse(serverTime || ''))
    ? new Date(Date.parse(serverTime)).toISOString()
    : undefined;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRY_UPDATED_EVENT, {
    detail: {
      expiresAt: new Date(expiration).toISOString(),
      ...(typeof normalizedCanExtend === 'boolean' ? { canExtend: normalizedCanExtend } : {}),
      ...(normalizedServerTime ? { serverTime: normalizedServerTime } : {}),
    },
  }));
};

const resetSessionExpiredState = () => {
  sessionExpiredDispatched = false;
  try {
    window.sessionStorage.removeItem(SESSION_NOTICE_KEY);
  } catch {
    // Storage may be disabled; the in-memory state is still reset.
  }
};

async function apiFetch(url, options = {}) {
  const { retry, suppressABASphereErrorEvent = false, ...fetchOptions } = options;
  const method = String(fetchOptions.method || 'GET').toUpperCase();
  const identifiedOptions = withAbaSphereMutationIdentity(url, method, fetchOptions);
  const fingerprint = identifiedOptions.mutationFingerprint;
  delete identifiedOptions.mutationFingerprint;
  const canRetry = retry !== false && ['GET', 'HEAD'].includes(method);
  const maximumAttempts = canRetry ? 2 : 1;
  let lastResponse;

  for (let attempt = 1; attempt <= maximumAttempts; attempt += 1) {
    if (fetchOptions.signal?.aborted) throw new DOMException('Request cancelled', 'AbortError');
    try {
      lastResponse = await fetch(url, { credentials: 'include', ...identifiedOptions });
      let invalidSessionResponse = false;
      if (lastResponse.status === 403 && !isCredentialRequest(url) && lastResponse.clone) {
        try {
          invalidSessionResponse = Boolean(invalidSessionReason(await lastResponse.clone().json()));
        } catch {
          // Non-JSON permission responses remain ordinary in-page 403 errors.
        }
      }
      if ((lastResponse.status === 401 || invalidSessionResponse) && !isCredentialRequest(url)) {
        notifySessionExpired();
      } else if (lastResponse.status !== 401) {
        // Authentication middleware can refresh the idle deadline before a
        // downstream handler returns 403/404/5xx. The presence of the signed
        // session headers, rather than response.ok, is authoritative.
        notifySessionExpiryUpdated(lastResponse.headers?.get?.(SESSION_EXPIRES_HEADER), {
          canExtend: lastResponse.headers?.get?.(SESSION_CAN_EXTEND_HEADER),
          serverTime: lastResponse.headers?.get?.(SERVER_TIME_HEADER),
        });
      }
      if (!retryableStatuses.has(lastResponse.status) || attempt === maximumAttempts) {
        if (!lastResponse.ok && !suppressABASphereErrorEvent
          && lastResponse.status !== 401 && !invalidSessionResponse) {
          await notifyABASphereApiError({ url, method, response: lastResponse });
        }
        if (fingerprint && (lastResponse.ok || !retryableStatuses.has(lastResponse.status))) {
          pendingMutationIdentities.delete(fingerprint);
        }
        return lastResponse;
      }
    } catch (error) {
      if (error.name === 'AbortError' || fetchOptions.signal?.aborted) throw error;
      if (attempt === maximumAttempts) {
        if (!suppressABASphereErrorEvent) await notifyABASphereApiError({ url, method, error });
        throw error;
      }
    }
    await wait(200 * attempt);
  }

  return lastResponse;
}

export {
  API_BASE,
  SESSION_EXPIRED_EVENT,
  SESSION_EXPIRY_UPDATED_EVENT,
  SESSION_EXPIRED_MESSAGE,
  SESSION_CAN_EXTEND_HEADER,
  SESSION_EXPIRES_HEADER,
  SERVER_TIME_HEADER,
  SESSION_NOTICE_KEY,
  ABASPHERE_API_ERROR_EVENT,
  notifySessionExpired,
  notifySessionExpiryUpdated,
  resetSessionExpiredState,
  withAbaSphereMutationIdentity,
};
export default apiFetch;
